import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
  TextField,
  Dialog,
  DialogContent,
  useMediaQuery,
  useTheme,
  CircularProgress, // <-- Add this import
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HeaderSection from "./HeaderSection";  

// Function to get only available products
const getAvailableProducts = (products) => {
  return products.filter(product => product.isAvailable);
};

export { getAvailableProducts }; // Export the function

const ProductCard = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [openAvailablePopup, setOpenAvailablePopup] = useState(false);
  const [openUnavailablePopup, setOpenUnavailablePopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true); // <-- Add loading state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get("https://enzo-matr.onrender.com/api/products"); // Fetch all products
        setProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false); // <-- Set loading to false after fetch
      }
    };
  
    fetchProducts();
  }, []);
  
 
  // Filter products based on search query
  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Open popup for available products
  const handleOpenAvailablePopup = (product) => {
    setSelectedProduct(product);
    setOpenAvailablePopup(true);
  };

  // Open popup for unavailable products
  const handleOpenUnavailablePopup = (product) => {
    setSelectedProduct(product);
    setOpenUnavailablePopup(true);
  };

  // Set the availability status
  const handleSetAvailability = async (status) => {
    try {
      // Update the product's availability in the backend
      const response = await axios.put(`https://enzo-matr.onrender.com/api/products/${selectedProduct._id}`, {
        isAvailable: status,
      });

      if (response.status === 200) {
        // Update the product's availability in the UI
        setProducts((prevProducts) =>
          prevProducts.map((p) =>
            p._id === selectedProduct._id ? { ...p, isAvailable: status } : p
          )
        );

        setOpenAvailablePopup(false);
        setOpenUnavailablePopup(false);
      } else {
        console.error("Failed to update availability in the backend");
      }
    } catch (error) {
      console.error("Failed to update availability:", error);
    }
  };


  return (
    <>
      {/* Header Section */}
      <HeaderSection />
      <Box
        sx={{
          bgcolor: "#20a4a3",
          p: 1,
          textAlign: "center",
          marginBottom: "10px",
          marginTop: "-10px",
          position: "relative",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            color: "white",
            fontFamily: "caladea",
            fontSize: isMobile ? "20px" : isTablet ? "28px" : "32px",
          }}
        >
          PRODUCT
        </Typography>
      </Box>
      <Box
        sx={{
          p: 3,
          bgcolor: "white",
          height: isMobile ? "auto" : "75vh",
          marginTop: "-11px",
        }}
      >
        {/* Show loading spinner */}
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "60vh" }}>
            <CircularProgress size={60} color="primary" />
            <Typography sx={{ mt: 2, color: "#333", fontWeight: "bold" }}>loading...</Typography>
          </Box>
        ) : (
          <>
            {/* Search Bar */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                backgroundColor: "#fff",
                borderRadius: 3,
                border: "1px solid black",
                width: isMobile ? "100%" : 450,
                height: 45,
                boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
                px: 2,
                mb: 3,
              }}
            >
              <TextField
                variant="standard"
                placeholder="Search Categories, Item"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{ disableUnderline: true }}
                sx={{
                  fontSize: 16,
                  "& input": { padding: "8px 10px" },
                  mb: isMobile ? 2 : 0,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#333",
                  borderRadius: 2,
                  width: 38,
                  height: 38,
                  ml: isMobile ? 0 : -2,
                  cursor: "pointer",
                  position: isMobile ? "static" : "relative",
                  left: isMobile ? 0 : 60,
                }}
              >
                <SearchIcon sx={{ color: "#fff" }} />
              </Box>
            </Box>

            {/* Product Grid */}
            <Box
              sx={{
                maxHeight: isMobile ? "auto" : "65vh", // Set a max height for scrolling
                overflowY: "auto",
                // Hide scrollbar for Chrome, Safari and Opera
                "&::-webkit-scrollbar": { display: "none" },
                // Hide scrollbar for IE, Edge and Firefox
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <Grid
                container
                spacing={isMobile ? 1 : 2}
                justifyContent={isMobile ? "flex-start" : "center"}
              >
                {filteredProducts.map((product) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={12 / 7}
                    key={product._id}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Card
                      sx={{
                        position: "relative",
                        borderRadius: 3,
                        width: isMobile ? "100%" : 180,
                        height: isMobile
                          ? "auto"
                          : "calc(84vh / 3 - 24px)",
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                        bgcolor: "#fff",
                        padding: 1,
                      }}
                    >
                      {/* Show Unavailable Ribbon ONLY when isAvailable is FALSE */}
                      {!product.isAvailable && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 5,
                            left: -45,
                            height: "13px",
                            width: "100px",
                            bgcolor: "red",
                            color: "#fff",
                            px: 2,
                            py: 2,
                            fontSize: 12,
                            fontFamily: "bricolage grotesque ",
                            textAlign: "center",
                            fontWeight: "bold",
                            transform: "rotate(-50deg)",
                            zIndex: 1,
                          }}
                        >
                          Unavailable
                        </Box>
                      )}

                      <CardMedia
                        component="img"
                        image={product.image}
                        alt={product.name}
                        sx={{
                          width: "100%",
                          height: isMobile ? "200px" : "50%",
                          borderRadius: 2,
                          objectFit: "cover",
                        }}
                      />
                      <CardContent
                        sx={{
                          textAlign: "center",
                          width: "100%",
                          p: 0,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "bold",
                            color: "red",
                            fontSize: 14,
                            textDecoration: "underline",
                          }}
                        >
                          {product.name}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Typography>Amount: {product.price}</Typography>
                          <Typography>Tax: {product.vat}%</Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 1,
                          }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              bgcolor: "#25bdbe",
                              fontSize: 10,
                              px: 3,
                              "&:hover": { bgcolor: "#1a9fa1" },
                            }}
                            onClick={() => handleOpenAvailablePopup(product)}
                          >
                            Available
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              bgcolor: "#333",
                              fontSize: 10,
                              px: 2,
                              ml: 1,
                              "&:hover": { bgcolor: "#222" },
                            }}
                            onClick={() => handleOpenUnavailablePopup(product)}
                          >
                            Unavailable
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Popup Dialog for Available Product */}
            {selectedProduct && openAvailablePopup && (
              <Dialog
                open={openAvailablePopup}
                onClose={() => setOpenAvailablePopup(false)}
                maxWidth="sm"
                sx={{
                  "& .MuiDialog-paper": {
                    borderRadius: "25px", // This sets the border radius of the external popup card
                    // Custom width of the popup
                    maxHeight: "400px", // Custom max height
                    height: "250px",
                    padding: -4,
                    marginBottom: 6,
                    boxShadow: 4, // Dynamic height
                  },
                }}
              >
                <DialogContent
                  sx={{
                    width: "440px",
                    height: "270px",
                    borderRadius: 1,
                    boxShadow: 7,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 2,
                    textAlign: "center",
                    bgcolor: "",
                    marginTop: -2,
                    position: "relative",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      marginTop: "40px",
                      fontSize: 14,
                      fontFamily: "",
                      ml: -12,
                      textAlign: "center",
                      width: "100%",
                      color: "#333",
                    }}
                  >
                    Are you sure this item is
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      fontFamily: "bricolage grotesque",
                      fontSize: 45,
                      textAlign: "center",
                      width: "90%",
                      color: "#333",
                      marginBottom: -9,
                      ml: -13,
                    }}
                  >
                    Available Now
                  </Typography>
                  <CardMedia
                    component="img"
                    image={selectedProduct.image}
                    alt="product"
                    sx={{
                      width: "100px", // Fixed width
                      height: "100px", // Fixed height
                      objectFit: "cover", // Ensure the image fills the container
                      borderRadius: "50%", // Rounded image
                      position: "absolute",
                      left: "70%",
                      bottom: "34%",
                      marginY: 2,
                      boxShadow: 3,
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 2, mt: 14 }}>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "#25bdbe",
                        color: "#fff",
                        fontWeight: "bold",
                        width: "180px",
                        fontSize: 16,
                        borderRadius: 2,
                        fontFamily: "caladea",
                        "&:hover": {
                          bgcolor: "#1a9fa1",
                        },
                      }}
                      onClick={() => handleSetAvailability(true)}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "#333",
                        color: "#fff",
                        width: "180px",
                        fontSize: 16,
                        borderRadius: 2,
                        fontFamily: "caladea",
                        "&:hover": {
                          bgcolor: "#222",
                        },
                      }}
                      onClick={() => setOpenAvailablePopup(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </DialogContent>
              </Dialog>
            )}

            {/* Popup Dialog for Unavailable Product */}
            {selectedProduct && openUnavailablePopup && (
              <Dialog
                open={openUnavailablePopup}
                onClose={() => setOpenUnavailablePopup(false)}
                maxWidth="sm"
                sx={{
                  "& .MuiDialog-paper": {
                    borderRadius: "30px", // This sets the border radius of the external popup card
                    // Custom width of the popup
                    maxHeight: "400px", // Custom max height
                    height: "auto", // Dynamic height
                  },
                }}
              >
                <DialogContent
                  sx={{
                    width: "440px",
                    borderRadius: 5,
                    boxShadow: 7,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 4,
                    textAlign: "center",
                    bgcolor: "",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: 14,
                      position: "relative",
                      color: "#333",
                      textAlign: "left",
                      width: "100%",
                      top:"80px",
                      left:"20px",
                    }}
                  >
                    Are you sure this item is
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      fontFamily: "bricolage grotesque",
                      textAlign: "left",
                      width: "100%",
                      color: "#333",
                    }}
                  >
                    Unavailable?
                  </Typography>

                  <CardMedia
                    component="img"
                    image={selectedProduct.image}
                    alt="Unavailable"
                    sx={{
                      width: "200px", // Fixed width
                      height: "200px", // Fixed height
                      objectFit: "cover", // Ensure the image fills the container
                      borderRadius: "15px", // Rounded corners
                      position: "absolute",
                      left: "55%",
                      bottom:"70px",
                      marginY: 2,
                      boxShadow: 3,
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 2, mt: 20 }}>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "#25bdbe",
                        color: "#fff",
                        fontWeight: "bold",
                        width: "190px",
                        fontSize: 16,
                        borderRadius: 2,
                        fontFamily: "caladea",
                        "&:hover": {
                          bgcolor: "#1a9fa1",
                        },
                      }}
                      onClick={() => handleSetAvailability(false)}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "#333",
                        color: "#fff",
                        width: "190px",
                        fontSize: 16,
                        borderRadius: 2,
                        fontFamily: "caladea",
                        "&:hover": {
                          bgcolor: "#222",
                        },
                      }}
                      onClick={() => setOpenUnavailablePopup(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </DialogContent>
              </Dialog>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default ProductCard;